import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { photoSafetyService } from '@/services/photoSafetyService';

export function ModelTestPage() {
  const [status, setStatus] = useState<string>('Not started');
  const [logs, setLogs] = useState<string[]>([]);
  const [detectionResult, setDetectionResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toISOString()}: ${message}`]);
  };

  const testModelLoading = async () => {
    setIsLoading(true);
    setLogs([]);
    addLog('Starting model loading test...');
    addLog('NOTE: Check browser DevTools Console for detailed error messages!');
    setStatus('Loading model...');

    try {
      // Initialize the service
      await photoSafetyService.initialize();
      addLog('Model initialized successfully');
      setStatus('Model loaded');

      // Check if model actually loaded
      const testResult = await photoSafetyService.checkPhotoSafety(new Blob(['test']));
      if (testResult.modelUsed === 'none') {
        addLog('⚠️ WARNING: Model returned "none" - it may not have loaded correctly!');
        addLog('Check browser console for errors from Transformers.js');
      } else {
        addLog(`✅ Model is working: ${testResult.modelUsed}`);
      }
    } catch (error) {
      addLog(`Model loading failed: ${error}`);
      addLog(`Error details: ${error instanceof Error ? error.stack : 'Unknown'}`);
      setStatus('Failed');
    }

    setIsLoading(false);
  };

  const testWithSampleImage = async () => {
    if (!window.confirm('This will download ~156MB model if not cached. Continue?')) {
      return;
    }

    setIsLoading(true);
    setLogs([]);
    addLog('Starting test with sample image...');

    try {
      // Test with a classroom image from Unsplash
      const testImageUrl = 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=400';
      addLog(`Fetching test image: ${testImageUrl}`);

      const response = await fetch(testImageUrl);
      const blob = await response.blob();
      addLog(`Image fetched: ${blob.size} bytes, type: ${blob.type}`);

      addLog('Running safety check...');
      const result = await photoSafetyService.checkPhotoSafety(blob);

      addLog(`Safety check completed in ${result.processingTimeMs}ms`);
      addLog(`Model used: ${result.modelUsed}`);
      addLog(`Results: ${JSON.stringify(result.schoolContentDetected)}`);

      setDetectionResult(result);
      setStatus('Test completed');
    } catch (error) {
      addLog(`Test failed: ${error}`);
      setStatus('Test failed');
    }

    setIsLoading(false);
  };

  const checkCacheStatus = async () => {
    setLogs([]);
    addLog('Checking browser cache status...');

    if (!('caches' in window)) {
      addLog('Cache API not available in this browser');
      return;
    }

    try {
      const cacheNames = await caches.keys();
      addLog(`Found ${cacheNames.length} cache(s)`);

      let totalModelFiles = 0;
      let totalSize = 0;

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();

        const modelFiles = requests.filter(req =>
          req.url.includes('huggingface') ||
          req.url.includes('onnx') ||
          req.url.includes('transformers')
        );

        if (modelFiles.length > 0) {
          addLog(`\nCache: ${cacheName}`);
          addLog(`  Model files: ${modelFiles.length}`);

          for (const req of modelFiles) {
            const response = await cache.match(req);
            if (response) {
              const blob = await response.blob();
              totalSize += blob.size;
              addLog(`  - ${req.url.split('/').pop()}: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
            }
          }

          totalModelFiles += modelFiles.length;
        }
      }

      addLog(`\nTotal model files cached: ${totalModelFiles}`);
      addLog(`Total cache size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

      // Check IndexedDB for Transformers.js models
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          const tfDbs = databases.filter(db =>
            db.name?.includes('transformers') ||
            db.name?.includes('onnx')
          );

          if (tfDbs.length > 0) {
            addLog('\nIndexedDB databases:');
            tfDbs.forEach(db => {
              addLog(`  - ${db.name} (v${db.version})`);
            });
          }
        } catch (e) {
          addLog('Could not check IndexedDB: ' + e);
        }
      }
    } catch (error) {
      addLog(`Cache check failed: ${error}`);
    }
  };

  const clearModelCache = async () => {
    if (!window.confirm('This will clear all cached models. Continue?')) {
      return;
    }

    setLogs([]);
    addLog('Clearing model cache...');

    try {
      // Clear browser caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
          addLog(`Deleted cache: ${cacheName}`);
        }
      }

      // Clear IndexedDB
      if ('indexedDB' in window) {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name && (db.name.includes('transformers') || db.name.includes('onnx'))) {
            indexedDB.deleteDatabase(db.name);
            addLog(`Deleted IndexedDB: ${db.name}`);
          }
        }
      }

      // Clear localStorage items related to transformers
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('transformers') || key.includes('huggingface'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        addLog(`Removed localStorage: ${key}`);
      });

      addLog('Cache cleared successfully');
      addLog('Please reload the page for changes to take effect');
    } catch (error) {
      addLog(`Failed to clear cache: ${error}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Transformers.js Model Test Page</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span>Status:</span>
                <span className="font-bold text-lg">
                  {isLoading ? '⏳ ' : ''}
                  {status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={testModelLoading}
                  disabled={isLoading}
                  variant="outline"
                >
                  Test Model Loading
                </Button>

                <Button
                  onClick={testWithSampleImage}
                  disabled={isLoading}
                  variant="default"
                >
                  Test with Sample Image
                </Button>

                <Button
                  onClick={checkCacheStatus}
                  disabled={isLoading}
                  variant="outline"
                >
                  Check Cache Status
                </Button>

                <Button
                  onClick={clearModelCache}
                  disabled={isLoading}
                  variant="destructive"
                >
                  Clear Model Cache
                </Button>
              </div>

              {detectionResult && (
                <Card>
                  <CardHeader>
                    <CardTitle>Detection Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-sm bg-gray-100 p-2 rounded overflow-auto">
                      {JSON.stringify(detectionResult, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Console Logs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-black text-green-400 p-4 rounded font-mono text-xs max-h-96 overflow-y-auto">
                    {logs.length === 0 ? (
                      <div className="text-gray-500">No logs yet. Click a button to start testing.</div>
                    ) : (
                      logs.map((log, index) => (
                        <div key={index} className="mb-1">
                          {log}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-yellow-50">
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">Important Notes:</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>The OWL-ViT model is ~156MB (quantized INT8 version)</li>
                    <li>First load will download from HuggingFace</li>
                    <li>Check browser DevTools Network tab to see downloads</li>
                    <li>Check DevTools Console for detailed logs</li>
                    <li>Model should cache after first download</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}